import type { ApiError } from './api'

/** A resource as every component showing it sees it; `answered` is the path its data or error answers. */
export interface Snapshot<T = unknown> {
  data?: T
  error?: ApiError
  answered?: string
  loading: boolean
}

/** Loads a resource: by GET, or by POST with `json`; `background` for a poll (AUTH-08). */
export type Loader = (path: string, json: unknown, background: boolean) => Promise<unknown>

interface Entry {
  path: string
  json: unknown
  snapshot: Snapshot
  listeners: Set<() => void>
  pending: boolean
  /** Asked to load again while a load was on its way, whose answer may predate a change. */
  again: boolean
}

/** Before a resource's first load: nothing yet, and loading. */
export const LOADING: Snapshot = { loading: true }

/**
 * The resources the app shows, each loaded once for all components that show it: one request
 * answers them all, a component shown again starts from the last answer while it loads afresh, and
 * a reload during a load loads again once that one answered. Resources no component shows are kept
 * for when one does, the `keep` most recent of them.
 */
export function createResourceCache(load: Loader, onError: (error: unknown) => ApiError, keep = 100) {
  const entries = new Map<string, Entry>()

  const set = (entry: Entry, snapshot: Snapshot) => {
    entry.snapshot = snapshot
    for (const listener of entry.listeners) listener()
  }

  const fetch = (entry: Entry, background: boolean) => {
    if (entry.pending) {
      entry.again = true
      return
    }
    entry.pending = true
    set(entry, { ...entry.snapshot, loading: true })
    load(entry.path, entry.json, background).then(
      (data) => settle(entry, { data, answered: entry.path, loading: false }),
      (error: unknown) => settle(entry, { error: onError(error), answered: entry.path, loading: false }),
    )
  }

  const settle = (entry: Entry, snapshot: Snapshot) => {
    entry.pending = false
    if (entry.again) {
      entry.again = false
      fetch(entry, false)
      return
    }
    set(entry, snapshot)
  }

  /** Forgets the oldest resources no component shows, beyond the `keep` most recent. */
  const prune = () => {
    let unused = [...entries.values()].filter((entry) => entry.listeners.size === 0).length
    for (const [key, entry] of entries) {
      if (unused <= keep) break
      if (entry.listeners.size === 0 && !entry.pending) {
        entries.delete(key)
        unused -= 1
      }
    }
  }

  return {
    snapshot: (key: string): Snapshot => entries.get(key)?.snapshot ?? LOADING,

    /** Shows the resource to `listener` until the returned function is called, loading it afresh. */
    subscribe(key: string, path: string, json: unknown, listener: () => void): () => void {
      let entry = entries.get(key)
      if (entry === undefined) {
        entry = { path, json, snapshot: LOADING, listeners: new Set(), pending: false, again: false }
        entries.set(key, entry)
      }
      const shown = entry
      if (shown.listeners.size === 0 && !shown.pending) fetch(shown, false)
      shown.listeners.add(listener)
      return () => {
        shown.listeners.delete(listener)
        if (shown.listeners.size > 0) return
        entries.delete(key) // the most recently used goes last, and is forgotten last
        entries.set(key, shown)
        prune()
      }
    },

    reload: (key: string) => {
      const entry = entries.get(key)
      if (entry) fetch(entry, false)
    },

    /** Loads again in the background, unless a load is on its way, whose answer it waits for. */
    poll: (key: string) => {
      const entry = entries.get(key)
      if (entry && !entry.pending) fetch(entry, true)
    },

    /** After a change: loads every shown resource under the path again, and forgets the others. */
    invalidate(prefix: string) {
      for (const [key, entry] of entries) {
        if (!entry.path.startsWith(prefix)) continue
        if (entry.listeners.size > 0 || entry.pending) fetch(entry, false) // a load on its way may predate the change
        else entries.delete(key)
      }
    },
  }
}
